import { wishlistsApi } from "@capone/common";
import { STAYS_WISHLIST } from "@capone/experiments";
import { Box, Button } from "@material-ui/core";
import clsx from "clsx";
import {
  Icon,
  IconName,
  genericPriceFormatter,
  getTotalPriceText,
  getDisplayNightlyPrices,
} from "halifax";
import React from "react";
import { useSelector } from "react-redux";
import {
  ListingSearchResult,
  Lodging,
  LodgingCollectionEnum,
  LodgingId,
  VRAvailabilityResultEnum,
} from "redmond";
import {
  AVAILABLE,
  getExperimentVariant,
  useExperiments,
} from "../../../../../../context/experiments";
import { ISetPropertyIdInFocus } from "../../../../actions/actions";
import { AvailabilityMapHotelTooltip } from "../AvailabilityMapHotelTooltip";
import { ConnectedAvailabilityMapPricePinProps } from "./container";
import "./styles.scss";

export interface IAvailabilityMapPricePinProps
  extends ConnectedAvailabilityMapPricePinProps {
  property: Lodging | ListingSearchResult;
  isInFocus: boolean;
  isHovered: boolean;
  isPreviouslyShown: boolean;
  isPreview?: boolean;
  isDesktop?: boolean;
  setPropertyIdInFocus: (lodgingId: LodgingId | null) => ISetPropertyIdInFocus;
  goToPropertyDetails: (propertyId: LodgingId | string) => void;
}

export const AvailabilityMapPricePin = (
  props: IAvailabilityMapPricePinProps
) => {
  const {
    property,
    isInFocus,
    isHovered,
    isPreviouslyShown,
    setPropertyIdInFocus,
    isPreview,
    isDesktop,
    goToPropertyDetails,
  } = props;

  const [hovered, setHovered] = React.useState(isHovered);
  React.useEffect(() => {
    setHovered(isHovered);
  }, [isHovered]);

  const propertyId =
    "lodging" in property ? property.lodging.id : property.listingId.id;
  const onClickPricePin = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPropertyIdInFocus(isInFocus ? null : propertyId);
  };

  const isUnavailableVR = React.useMemo(
    () =>
      "availability" in property
        ? property.availability.AvailabilityResult ===
          VRAvailabilityResultEnum.Unavailable
        : false,
    [property]
  );

  const propertyPrice = React.useMemo(
    () =>
      "availability" in property
        ? property.availability.rate?.price
        : property.price,
    [property]
  );

  const collection =
    "lodging" in property
      ? property.lodgingCollection
      : property.listing.listingCollection;
  const showPCStyling = collection === LodgingCollectionEnum.Premier;
  const showLCStyling = collection === LodgingCollectionEnum.Lifestyle;

  const expState = useExperiments();
  const wishlistExperiment = getExperimentVariant(
    expState.experiments,
    STAYS_WISHLIST
  );
  const isWishlistExperiment = React.useMemo(
    () => wishlistExperiment === AVAILABLE,
    [wishlistExperiment]
  );

  const isWishlisted = useSelector(
    wishlistsApi.endpoints.getAllWishlistItemsByUser.select()
  )?.data?.has(propertyId);

  if (propertyPrice || isUnavailableVR) {
    return (
      <Box
        className={clsx("lodging-marker-wrapper", {
          inFocus: isInFocus,
          hovered: isInFocus || hovered,
          previouslyShown: isPreviouslyShown,
          "premier-collection": showPCStyling,
          "lifestyle-collection": showLCStyling,
        })}
      >
        {isInFocus && isDesktop && !isUnavailableVR && (
          <AvailabilityMapHotelTooltip
            onClickHotelDetails={(e) => {
              e.stopPropagation();
              setPropertyIdInFocus(propertyId);
              goToPropertyDetails(propertyId);
            }}
            onCloseClick={(e) => {
              e.stopPropagation();
              setPropertyIdInFocus(null);
            }}
            lodging={property}
          />
        )}
        <Button
          className="pc-price-pin-wrapper price-pin"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={(e) => (!isPreview ? onClickPricePin(e) : null)}
        >
          {isUnavailableVR ? (
            <Icon name={IconName.UnavailableMapPin} />
          ) : (
            <>
              <Icon name={IconName.PremierCollectionPricePin} />
              <Box className="price-pin-label">
                {isWishlisted && isWishlistExperiment && (
                  <Icon
                    width="15px"
                    height="15px"
                    name={IconName.WishlistHeart}
                    className="icon-fill-red"
                  />
                )}
                {"lodging" in property
                  ? getTotalPriceText({
                      price:
                        getDisplayNightlyPrices(property.price!)?.fiat ??
                        property.price!.nightlyPrice.fiat,
                      priceFormatter: genericPriceFormatter,
                    })
                  : getTotalPriceText({
                      price: property.availability.rate!?.price.nightly.fiat,
                      priceFormatter: genericPriceFormatter,
                    })}
              </Box>
            </>
          )}
        </Button>
      </Box>
    );
  }

  return null;
};
